import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Card,
  CardBody,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Form,
  Label,
  Input,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import i18next from "i18next";
import Select from "react-select";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import { DebounceInput } from "react-debounce-input";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import {
  addTechnician,
  getTechnicians,
  getTechnician,
  updateTechnician,
  deleteTechnician,
  toggleTechnicianModalAction,
  toggleTechnicianBlockModalAction,
  updateBlockStatus,
  getTechnicianPurchases,
  changeTechnicianRemainingCalls,
} from "../../store/users/technicians/actions";
import BreadCrumb from "../../Components/Common/BreadCrumb";

import {
  sendNotification,
  toggleNotificationModalAction,
} from "../../store/notification/actions";
import { getCategoriesSelectList } from "../../store/services/categories/actions";

const TechniciansPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    // technicians
    technicians,
    loadingTechnicians,
    technician,
    // add
    addTechnicianError,
    addTechnicianLoading,
    addTechnicianSuccess,
    // update
    updateTechnicianLoading,
    updateTechnicianSuccess,
    // get
    getTechnicianSuccess,
    // block status
    updatingBlockStatus,
    updateBlockStatusSuccess,
    updateBlockStatusError,
    // purchases
    technicianPurchases,
    technicianPurchasesLoading,
    // change technician remaining calls
    changeTechnicianRemainingCallsLoading,
    changeTechnicianRemainingCallsError,
    changeTechnicianRemainingCallsSuccess,
  } = useSelector((state) => state.Technicians);

  document.title = `${t("Technicians.Technicians")} | ${t(
    process.env.REACT_APP_HEADING
  )}`;

  const [isTechnicianModalOpen, setIsTechnicianModalOpen] = useState(false);
  const [selectedServiceCategories, setSelectedServiceCategories] = useState();
  const { categoriesSelectList } = useSelector((state) => state.Categories);
  const [invalidServiceCategoryField, setInvalidServiceCategoryField] =
    useState(false);
  const [hasPurchased, setHasPurchased] = useState(true);
  const [purchasesTechnicianId, setPurchasesTechnicianId] = useState();

  const toggleTechnicianModal = () => {
    if (!isTechnicianModalOpen) {
      dispatch(toggleTechnicianModalAction());
      setSelectedServiceCategories(null);
      setInvalidServiceCategoryField(false);
      // reset on modal open
      technicianForm.resetForm({
        firstName: "",
        lastName: "",
        email: "",
        mobile: "",
        password: "",
        confirmPassword: "",
      });
    }
    setIsTechnicianModalOpen(!isTechnicianModalOpen);
  };

  const [isTechnicianDeleteModalOpen, setIsTechnicianDeleteModalOpen] =
    useState(false);

  const toggleTechnicianDeleteModal = () => {
    setIsTechnicianDeleteModalOpen(!isTechnicianDeleteModalOpen);
  };

  const [request, setRequest] = useState({
    limit: 10,
    page: 1,
    sortBy: "createdAt",
    sortDirection: "DESC",
  });

  const [totalRows, setTotalRows] = useState(0);

  const columns = [
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Fields.First Name")}</span>
      ),
      selector: (row) => row.firstName,
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Fields.Last Name")}</span>
      ),
      selector: (row) => row.lastName,
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Fields.Mobile")}</span>
      ),
      selector: (row) => (
        <a href={`tel:${row.mobile}`} title={row.mobile}>
          {row.mobile}
        </a>
      ),
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">{t("Fields.Email")}</span>,
      selector: (row) =>
        row.email ? (
          <a
            style={{ wordWrap: "break-word", whiteSpace: "pre-wrap" }}
            href={`mailto:${row.email}`}
            title={row.email}
          >
            {row.email}
          </a>
        ) : (
          " - "
        ),
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">
          {t("Categories.Categories")}
        </span>
      ),
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">
            <a
              style={{ cursor: "pointer" }}
              className="link"
              title={t("Technicians.View Categories")}
              onClick={() => {
                onGetTechnicianPurchases(row.id);
              }}
            >
              {t("Technicians.View Categories")}
            </a>
          </div>
        </div>
      ),
      sortable: false,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Common.Created At")}</span>
      ),
      selector: (row) => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
      width: "180px",
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Fields.Mobile")}</span>
      ),
      selector: (row) => {
        switch (row.isMobileVerified) {
          case true:
            return (
              <span className="badge badge-soft-success">
                {" "}
                {t("Validations.Mobile.Verified")}{" "}
              </span>
            );
          default:
            return (
              <span className="badge badge-soft-danger">
                {t("Validations.Mobile.Not Verified")}{" "}
              </span>
            );
        }
      },
      sortable: false,
    },
    {
      name: <span className="font-weight-bold fs-13">{t("Common.Email")}</span>,
      selector: (row) => {
        switch (row.isEmailVerified) {
          case true:
            return (
              <span className="badge badge-soft-success">
                {" "}
                {t("Validations.Email.Verified")}{" "}
              </span>
            );
          default:
            return (
              <span className="badge badge-soft-danger">
                {t("Validations.Email.Not Verified")}{" "}
              </span>
            );
        }
      },
      sortable: false,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Common.Status")}</span>
      ),
      selector: (row) => {
        switch (row.isLocked) {
          case true:
            return (
              <span className="badge badge-soft-danger">
                {" "}
                {t("Common.Locked")}{" "}
              </span>
            );
          default:
            return (
              <span className="badge badge-soft-success">
                {" "}
                {t("Common.Active")}{" "}
              </span>
            );
        }
      },
      sortable: false,
    },
    {
      name: <span className="font-weight-bold fs-13">{t("User.Blocked")}</span>,
      selector: (row) => {
        switch (row.isBlock) {
          case true:
            return (
              <span className="badge badge-soft-danger">
                {" "}
                {t("Common.Yes")}{" "}
              </span>
            );
          default:
            return (
              <span className="badge badge-soft-success">
                {" "}
                {t("Common.No")}{" "}
              </span>
            );
        }
      },
      sortable: false,
      width: "200px",
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Common.Action")}</span>
      ),
      sortable: false,
      cell: (row) => {
        return (
          <React.Fragment>
            <Button
              color="primary"
              className="btn-icon btn-sm"
              onClick={() => onEdit(row.id)}
            >
              <i className="ri-pencil-fill" />
            </Button>
            &nbsp;
            <Button
              title={row.isBlock ? t("User.Unblock") : t("User.Block")}
              color={row.isBlock ? "warning" : "danger"}
              className="btn-icon btn-sm"
              onClick={() => toggleBlockUnblockModal(row.id)}
            >
              {row.isBlock ? (
                <i className="ri-checkbox-circle-line"></i>
              ) : (
                <i className="ri-forbid-line" />
              )}
            </Button>
            &nbsp;
            <Button
              color="danger"
              className="btn-icon btn-sm"
              onClick={() => onDelete(row.id)}
            >
              <i className="ri-delete-bin-5-line" />
            </Button>
          </React.Fragment>
        );
      },
    },
  ];

  const [rows, setRows] = useState([]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    let query = {
      limit: queryParams.has("limit") ? queryParams.get("limit") : 10,
      page: queryParams.has("page") ? queryParams.get("page") : 1,
      sortBy: queryParams.has("sortBy")
        ? queryParams.get("sortBy").split(":")[0]
        : "createdAt",
      sortDirection: queryParams.has("sortBy")
        ? queryParams.get("sortBy").split(":")[1]
        : "DESC",
      search: queryParams.has("search") ? queryParams.get("search") : "",
      hasPurchased: hasPurchased,
    };
    setDefaultSortAsc(query.sortDirection === "ASC");
    setDefaultSortFieldId(getSortByColumnId(query.sortBy));
    setRequest(query);
    dispatch(getTechnicians(query));
    dispatch(getCategoriesSelectList());
  }, []);

  useEffect(() => {
    const { limit, page, search, sortBy, sortDirection } = request;
    let query;
    if (page) query = `?page=${page}`;
    if (limit) query += `&limit=${limit}`;
    if (search && search !== "") {
      history.replace({
        search: `&search=${search}`,
      });
    }
    if (sortBy && sortDirection) query += `&sortBy=${sortBy}:${sortDirection}`;
    if (search) query += `&search=${search}`;
    history.replace({ search: query });
  }, [request]);

  useEffect(() => {
    if (
      getTechnicianSuccess &&
      technician &&
      technician.technicianCategories &&
      technician.technicianCategories.length > 0
    ) {
      setSelectedServiceCategories(
        technician.technicianCategories.map((category) => {
          return {
            value: category.id,
            label:
              i18next.language === "ar" ? category.arabicTitle : category.title,
          };
        })
      );
    } else {
      setSelectedServiceCategories(null);
    }
  }, [getTechnicianSuccess]);

  useEffect(() => {
    setRows(technicians?.data);
    setTotalRows(technicians.meta?.totalItems);
  }, [technicians, loadingTechnicians]);

  const onChangeRowsPerPage = (pageSize) => {
    setRequest({ ...request, limit: pageSize });
    dispatch(getTechnicians({ ...request, limit: pageSize }));
  };

  const onChangePage = (page) => {
    setRequest({ ...request, page: page });
    dispatch(getTechnicians({ ...request, page: page }));
  };

  const onSearch = (text) => {
    setRequest({ ...request, search: text });
    dispatch(getTechnicians({ ...request, search: text }));
  };

  useEffect(() => {
    if (addTechnicianSuccess === true || updateTechnicianSuccess === true) {
      toggleTechnicianModal();
      dispatch(getTechnicians(request));
    }
  }, [addTechnicianSuccess, updateTechnicianSuccess]);

  const getSortByColumnName = (id) => {
    let sortBy;
    switch (id) {
      case 1:
        sortBy = "firstName";
        break;
      case 2:
        sortBy = "lastName";
        break;
      case 3:
        sortBy = "mobile";
        break;
      case 4:
        sortBy = "email";
        break;
      case 6:
        sortBy = "createdAt";
        break;
      default:
        sortBy = "createdAt";
        break;
    }
    return sortBy;
  };

  const getSortByColumnId = (name) => {
    let sortBy;
    switch (name) {
      case "firstName":
        sortBy = 1;
        break;
      case "lastName":
        sortBy = 2;
        break;
      case "mobile":
        sortBy = 3;
        break;
      case "email":
        sortBy = 4;
        break;
      case "createdAt":
        sortBy = 6;
        break;
      default:
        sortBy = 6;
        break;
    }
    return sortBy;
  };

  const onSort = (column, sortDirection) => {
    const sortBy = getSortByColumnName(column.id);
    setRequest({
      ...request,
      sortBy: sortBy,
      sortDirection: sortDirection.toUpperCase(),
    });
    dispatch(
      getTechnicians({
        ...request,
        sortDirection: sortDirection.toUpperCase(),
        sortBy: sortBy,
      })
    );
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(6);
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);

  /**
   * Formik handling
   */
  const technicianForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: technician.id || "",
      firstName: technician.firstName || "",
      lastName: technician.lastName || "",
      email: technician.email || "",
      mobile: technician.mobile || "",
      password: technician.password || "",
      confirmPassword: technician.confirmPassword || "",
    },

    validationSchema: Yup.object({
      id: Yup.string().optional(),
      firstName: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.FirstName.Required")),
      lastName: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.LastName.Required")),
      email: Yup.string()
        .email(t("Validations.Email.Invalid"))
        .required(t("Validations.Email.Required")),
      mobile: Yup.string()
        .required(t("Validations.Mobile.Required"))
        // .max(8, t("Validations.Common.Max", { length: 8 }))
        .min(8, t("Validations.Common.Min", { length: 8 })),
      password: Yup.string()
        .min(8, t("Validations.Common.Min", { length: 8 }))
        .max(25, t("Validations.Common.Max", { length: 25 }))
        .when("id", (id) => {
          if (id === "" || id === null || id === undefined)
            return Yup.string()
              .min(8, t("Validations.Common.Min", { length: 8 }))
              .max(25, t("Validations.Common.Max", { length: 25 }))
              .required(t("Validations.NewPassword.Required"));
        }),
      confirmPassword: Yup.string()
        .min(8, t("Validations.Common.Min", { length: 8 }))
        .max(25, t("Validations.Common.Max", { length: 25 }))
        .oneOf([Yup.ref("password"), null], t("Validations.Password.Match"))
        .when("id", (id) => {
          if (id === "" || id === null || id === undefined)
            return Yup.string()
              .min(8, t("Validations.Common.Min", { length: 8 }))
              .max(25, t("Validations.Common.Max", { length: 25 }))
              .required(t("Validations.ConfirmPassword.Required"))
              .oneOf(
                [Yup.ref("password"), null],
                t("Validations.Password.Match")
              );
        }),
    }),

    onSubmit: (values) => {
      if (
        !selectedServiceCategories ||
        !selectedServiceCategories.length ||
        selectedServiceCategories.length === 0
      ) {
        setInvalidServiceCategoryField(true);
        setTimeout(() => {
          setInvalidServiceCategoryField(false);
        }, 2000);
        return;
      } else {
        setInvalidServiceCategoryField(false);
      }
      if (selectedTechnicianId)
        dispatch(
          updateTechnician({
            ...values,
            id: selectedTechnicianId,
            password: values.password ? values.password : null,
            confirmPassword: values.confirmPassword
              ? values.confirmPassword
              : null,
            serviceCategoryIds: selectedServiceCategories.map(
              (item) => item.value
            ),
          })
        );
      else
        dispatch(
          addTechnician({
            ...values,
            serviceCategoryIds: selectedServiceCategories.map(
              (item) => item.value
            ),
          })
        );
    },
  });

  const [selectedTechnicianId, setSelectedTechnicianId] = useState(null);

  const onEdit = (id) => {
    getCategories();
    setSelectedTechnicianId(id);
    dispatch(getTechnician(id));
    toggleTechnicianModal();
  };

  const onDelete = (id) => {
    setSelectedTechnicianId(id);
    toggleTechnicianDeleteModal();
  };

  const onDeleteConfirmation = () => {
    dispatch(deleteTechnician(selectedTechnicianId));
    toggleTechnicianDeleteModal();
    setTimeout(() => {
      dispatch(getTechnicians());
      setSelectedTechnicianId(null);
    }, 3000);
  };

  const onAdd = () => {
    getCategories();
    setSelectedTechnicianId(null);
    toggleTechnicianModal();
  };

  useEffect(() => {
    if (technician && technician.blockUntil) {
      setBlockUntil([technician.blockUntil]);
    }
  }, [technician]);

  //#region notification
  const {
    sendingNotification,
    sendingNotificationSuccess,
    sendingNotificationError,
    notification,
  } = useSelector((state) => state.Notifications);

  useEffect(() => {
    if (sendingNotificationSuccess) {
      toggleNotificationModal();
    }
  }, [sendingNotificationSuccess]);

  /**
   * Formik handling
   */
  const notificationForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: notification.title || "",
      arabicTitle: notification.arabicTitle || "",
      body: notification.body || "",
      arabicBody: notification.arabicBody || "",
    },

    validationSchema: Yup.object({
      title: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.Title.Required")),
      arabicTitle: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.ArabicTitle.Required")),
      body: Yup.string()
        .max(255, t("Validations.Common.Max", { length: 255 }))
        .required(t("Validations.Body.Required")),
      arabicBody: Yup.string()
        .max(255, t("Validations.Common.Max", { length: 255 }))
        .required(t("Validations.ArabicBody.Required")),
    }),

    onSubmit: (values) => {
      if (canSendNotification()) {
        dispatch(sendNotification({ ...values, ...toNotify }));
      }
    },
  });

  const [toNotify, setToNotify] = useState({});

  const onSelectionChange = (selection) => {
    if (selection.selectedCount > 0) {
      setToNotify({
        all: false,
        userId: selection.selectedRows.map((row) => row.id),
      });
    } else {
      setToNotify({ all: false, userId: [] });
    }
  };

  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

  const toggleNotificationModal = () => {
    if (!isNotificationModalOpen) {
      dispatch(toggleNotificationModalAction());
      // reset on  modal open
      notificationForm.resetForm({
        title: "",
        arabicTitle: "",
        body: "",
        arabicBody: "",
      });
    }
    setIsNotificationModalOpen(!isNotificationModalOpen);
  };

  const canSendNotification = () => {
    return (
      (toNotify && toNotify.all) ||
      (toNotify &&
        !toNotify.all &&
        toNotify.userId &&
        toNotify.userId.length > 0)
    );
  };

  const handleMulti = (selectedMulti) => {
    setSelectedServiceCategories(selectedMulti);
  };

  const getCategories = () => {
    dispatch(getCategoriesSelectList());
  };

  //#endregion

  const onChangeCategoryFilter = (value) => {
    value = value === "all" ? null : value;
    setRequest({ ...request, page: 1, categoryId: value });
    dispatch(
      getTechnicians({
        ...request,
        page: 1,
        categoryId: value,
      })
    );
  };

  const onChangeHasPurchased = (event) => {
    const {
      target: { checked },
    } = event;
    setHasPurchased(checked);
    setRequest({
      ...request,
      page: 1,
      hasPurchased: checked,
    });
    dispatch(
      getTechnicians({
        ...request,
        page: 1,
        hasPurchased: checked,
      })
    );
  };

  const countUP = (categoryId) => {
    dispatch(
      changeTechnicianRemainingCalls({
        categoryId: categoryId,
        technicianId: purchasesTechnicianId,
        actionType: "increment",
      })
    );
  };

  const countDown = (categoryId) => {
    dispatch(
      changeTechnicianRemainingCalls({
        categoryId: categoryId,
        technicianId: purchasesTechnicianId,
        actionType: "decrement",
      })
    );
  };

  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);

  const togglePurchasesModal = () => {
    setIsPurchaseModalOpen(!isPurchaseModalOpen);
  };

  const onGetTechnicianPurchases = (id) => {
    setPurchasesTechnicianId(id);
    dispatch(getTechnicianPurchases(id));
    togglePurchasesModal();
  };

  //#region Block Unblock Modal
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);

  const toggleBlockUnblockModal = (id) => {
    if (!isBlockModalOpen) {
      dispatch(toggleTechnicianBlockModalAction());
      setSelectedTechnicianId(id);
      dispatch(getTechnician(id));
      // reset on modal open
      blockUnblockForm.resetForm({
        isBlock: false,
        blockReason: "",
        blockReasonArabic: "",
        blockUntil: null,
      });
    }
    setIsBlockModalOpen(!isBlockModalOpen);
  };

  /**
   * Formik handling
   */
  const blockUnblockForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      isBlock: technician.isBlock || false,
      blockReason: technician.blockReason || "",
      blockReasonArabic: technician.blockReasonArabic || "",
      blockUntil: technician.blockUntil,
    },

    validationSchema: Yup.object({
      isBlock: Yup.bool().required(t("Validations.Block.Required")),
      blockReason: Yup.string().when("isBlock", {
        is: true,
        then: Yup.string()
          .max(1000, t("Validations.Common.Max", { length: 1000 }))
          .required(t("Validations.BlockReason.Required")),
      }),
      blockReasonArabic: Yup.string().when("isBlock", {
        is: true,
        then: Yup.string()
          .max(1000, t("Validations.Common.Max", { length: 1000 }))
          .required(t("Validations.BlockReasonArabic.Required")),
      }),
      blockUntil: Yup.date().optional().nullable(),
    }),

    onSubmit: (values) => {
      let request = {
        ...values,
        id: selectedTechnicianId,
      };
      if (blockUntil && blockUntil.length) {
        request = { ...request, blockUntil: blockUntil[0] };
      }
      if (selectedTechnicianId) dispatch(updateBlockStatus(request));
    },
  });

  const [blockUntil, setBlockUntil] = useState();

  useEffect(() => {
    if (updateBlockStatusSuccess) {
      toggleBlockUnblockModal();
      dispatch(getTechnicians(request));
    }
  }, [updateBlockStatusSuccess]);

  const onChangeIsBlock = (e) => {
    blockUnblockForm.handleChange(e);
  };

  //#endregion

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("Technicians.Technicians")}
            pageTitle={t("Common.Users")}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="g-4 mb-3">
                    <Col className="col-3">
                      <label>&nbsp;</label>
                      <div className="search-box ms-2">
                        <DebounceInput
                          className="form-control search"
                          placeholder={`${t("Common.Search")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(event) => onSearch(event.target.value)}
                          value={request.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col className="col-3">
                      <div className="search-box ms-2">
                        <label>{t("Common.Filter by Service Category")}</label>
                        <select
                          className="form-select mb-3"
                          aria-label="Default select example"
                          onChange={({ target }) =>
                            onChangeCategoryFilter(target.value)
                          }
                        >
                          <option value={"all"} key={null}>
                            {t("Common.All")}
                          </option>
                          {categoriesSelectList &&
                            categoriesSelectList.map(({ id, value }) => (
                              <option value={id} key={id}>
                                {i18n.language === "ar" ? value.ar : value.en}
                              </option>
                            ))}
                        </select>
                      </div>
                    </Col>
                    <Col className="col-sm-auto d-flex justify-content-center align-items-center">
                      <div className="search-box ms-2">
                        <div className="form-check mt-2">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id="hasPurchased"
                            onChange={onChangeHasPurchased}
                            checked={hasPurchased}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="hasPurchased"
                          >
                            {t("Common.Purchased Only")}
                          </Label>
                        </div>
                      </div>
                    </Col>
                    <Col className="col-sm">
                      <div className="d-flex justify-content-sm-end">
                        <Button
                          color="success"
                          className="add-btn me-1"
                          id="create-btn"
                          onClick={onAdd}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>{" "}
                          {t("Common.Add")}
                        </Button>
                        <Button
                          disabled={!canSendNotification()}
                          color="success"
                          className="add-btn me-1"
                          id="create-btn"
                          onClick={toggleNotificationModal}
                        >
                          <i className="ri-notification-2-line align-bottom me-1"></i>{" "}
                          {t("Common.Notify")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <DataTable
                    columns={columns}
                    data={rows}
                    progressPending={loadingTechnicians}
                    progressComponent={
                      <Spinner
                        size="sm"
                        className="flex-shrink-0 m-5"
                        role="status"
                      ></Spinner>
                    }
                    pagination
                    paginationServer
                    sortServer
                    onSort={onSort}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                    onChangePage={onChangePage}
                    defaultSortFieldId={defaultSortFieldId}
                    defaultSortAsc={defaultSortAsc}
                    persistTableHead
                    paginationTotalRows={totalRows}
                    selectableRows
                    onSelectedRowsChange={onSelectionChange}
                    paginationComponentOptions={{
                      rowsPerPageText: t("Pagination.Rows per page"),
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {/* Add Modal */}
      <Modal
        isOpen={isTechnicianModalOpen}
        toggle={() => {
          toggleTechnicianModal();
        }}
        centered
      >
        <ModalHeader className="bg-light p-3">
          {selectedTechnicianId
            ? t("Technicians.Edit Technician")
            : t("Technicians.Add Technician")}
          <Button
            type="button"
            onClick={() => {
              setIsTechnicianModalOpen(false);
            }}
            className="btn-close"
            aria-label={t("Common.Close")}
          ></Button>
        </ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            technicianForm.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalBody>
            {addTechnicianError && addTechnicianError ? (
              <Alert color="danger">
                {" "}
                {t(`Messages.Error.${addTechnicianError}`)}{" "}
              </Alert>
            ) : null}
            <div className="mb-3">
              <Label htmlFor="firstName" className="form-label">
                {t("Fields.First Name")}*
              </Label>
              <Input
                type="text"
                className="form-control"
                id="firstName"
                placeholder={t("Fields.Enter your first name")}
                value={technicianForm.values.firstName || ""}
                onChange={technicianForm.handleChange}
                onBlur={technicianForm.handleBlur}
                invalid={
                  technicianForm.touched.firstName &&
                  technicianForm.errors.firstName
                    ? true
                    : false
                }
              />
              {technicianForm.touched.firstName &&
              technicianForm.errors.firstName ? (
                <FormFeedback type="invalid">
                  {technicianForm.errors.firstName}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="lastName" className="form-label">
                {t("Fields.Last Name")}*
              </Label>
              <Input
                type="text"
                className="form-control"
                id="lastName"
                placeholder={t("Fields.Enter your last name")}
                value={technicianForm.values.lastName || ""}
                onChange={technicianForm.handleChange}
                onBlur={technicianForm.handleBlur}
                invalid={
                  technicianForm.touched.lastName &&
                  technicianForm.errors.lastName
                    ? true
                    : false
                }
              />
              {technicianForm.touched.lastName &&
              technicianForm.errors.lastName ? (
                <FormFeedback type="invalid">
                  {technicianForm.errors.lastName}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="mobile" className="form-label">
                {t("Fields.Mobile")}*
              </Label>
              <Input
                type="tel"
                className="form-control"
                id="mobile"
                // maxLength={8}
                minLength={8}
                placeholder={t("Fields.Enter your mobile")}
                value={technicianForm.values.mobile || ""}
                onChange={(e) => {
                  e.preventDefault();
                  const { value } = e.target;
                  const regex =
                    /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                  if (regex.test(value.toString())) {
                    technicianForm.setValues({
                      ...technicianForm.values,
                      mobile: value,
                    });
                  } else {
                    technicianForm.setValues({
                      ...technicianForm.values,
                      mobile: "",
                    });
                  }
                }}
                onBlur={technicianForm.handleBlur}
                invalid={
                  technicianForm.touched.mobile && technicianForm.errors.mobile
                    ? true
                    : false
                }
              />
              {technicianForm.touched.mobile && technicianForm.errors.mobile ? (
                <FormFeedback type="invalid">
                  {technicianForm.errors.mobile}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="email" className="form-label">
                {t("Fields.Email")}*
              </Label>
              <Input
                name="email"
                className="form-control"
                placeholder={t("Fields.Enter Email")}
                type="email"
                disabled={selectedTechnicianId}
                onChange={technicianForm.handleChange}
                onBlur={technicianForm.handleBlur}
                value={technicianForm.values.email || ""}
                invalid={
                  technicianForm.touched.email && technicianForm.errors.email
                    ? true
                    : false
                }
              />
              {technicianForm.touched.email && technicianForm.errors.email ? (
                <FormFeedback type="invalid">
                  {technicianForm.errors.email}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Label htmlFor="choices-multiple-default" className="form-label">
                {t("Fields.Service Category")}*
              </Label>
              <Select
                id="serviceCategoryIds"
                value={selectedServiceCategories}
                isMulti={true}
                onChange={(v) => {
                  handleMulti(v);
                }}
                options={categoriesSelectList.map((c) => ({
                  value: c.id,
                  label: i18next.language === "ar" ? c.value.ar : c.value.en,
                }))}
                placeholder={t("Common.Select")}
              />
              {invalidServiceCategoryField && (
                <FormFeedback type="invalid" className="d-block">
                  {t("Validations.ServiceCategory.Required")}
                </FormFeedback>
              )}
            </div>
            <div className="mb-3">
              <Label htmlFor="password" className="form-label">
                {t("Fields.New Password")}
                {!selectedTechnicianId ? "*" : `(${t("Common.Optional")})`}
              </Label>
              <Input
                type="password"
                className="form-control"
                id="password"
                placeholder={t("Fields.Enter New Password")}
                value={technicianForm.values.password || ""}
                onChange={technicianForm.handleChange}
                onBlur={technicianForm.handleBlur}
                invalid={
                  technicianForm.touched.password &&
                  technicianForm.errors.password
                    ? true
                    : false
                }
              />
              {technicianForm.touched.password &&
              technicianForm.errors.password ? (
                <FormFeedback type="invalid">
                  {technicianForm.errors.password}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Label htmlFor="confirmPassword" className="form-label">
                {t("Fields.Confirm Password")}
                {!selectedTechnicianId ? "*" : `(${t("Common.Optional")})`}
              </Label>
              <Input
                type="password"
                className="form-control"
                id="confirmPassword"
                placeholder={t("Fields.Enter Confirm Password")}
                value={technicianForm.values.confirmPassword || ""}
                onChange={technicianForm.handleChange}
                onBlur={technicianForm.handleBlur}
                invalid={
                  technicianForm.touched.confirmPassword &&
                  technicianForm.errors.confirmPassword
                    ? true
                    : false
                }
              />
              {technicianForm.touched.confirmPassword &&
              technicianForm.errors.confirmPassword ? (
                <FormFeedback type="invalid">
                  {technicianForm.errors.confirmPassword}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="hstack gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setIsTechnicianModalOpen(false)}
              >
                {t("Common.Close")}
              </button>
              <button
                type="submit"
                className="btn btn-success"
                id="add-btn"
                disabled={addTechnicianLoading || updateTechnicianLoading}
              >
                {addTechnicianLoading || updateTechnicianLoading ? (
                  <span className="d-flex align-items-center">
                    <span className="flex-grow-1 me-2">
                      {t("Common.Saving")}...
                    </span>
                    <Spinner size="sm" className="flex-shrink-0" role="status">
                      {" "}
                      {t("Common.Saving")}
                      ...{" "}
                    </Spinner>
                  </span>
                ) : (
                  <>{t("Common.Save")}</>
                )}
              </button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>
      {/* Remove Modal */}
      <Modal
        isOpen={isTechnicianDeleteModalOpen}
        toggle={() => {
          toggleTechnicianDeleteModal();
        }}
        centered
      >
        <div className="modal-header">
          <Button
            type="button"
            onClick={() => setIsTechnicianDeleteModalOpen(false)}
            className="btn-close"
            aria-label="Close"
          >
            {" "}
          </Button>
        </div>
        <ModalBody>
          <div className="mt-2 text-center">
            <lord-icon
              src="https://cdn.lordicon.com/gsqxdxog.json"
              trigger="loop"
              colors="primary:#f7b84b,secondary:#f06548"
              style={{ width: "100px", height: "100px" }}
            ></lord-icon>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("Common.Are you Sure?")}</h4>
              <p className="text-muted mx-4 mb-0">
                {t("Common.Are you Sure You want to Remove this Record?")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn w-sm btn-light"
              onClick={() => {
                setSelectedTechnicianId(null);
                toggleTechnicianDeleteModal();
              }}
            >
              {t("Common.Close")}
            </button>
            <button
              type="button"
              className="btn w-sm btn-danger "
              id="delete-record"
              onClick={() => onDeleteConfirmation()}
            >
              {t("Common.Yes, Delete It!")}
            </button>
          </div>
        </ModalBody>
      </Modal>
      {/* Notification Modal */}
      <Modal
        isOpen={isNotificationModalOpen}
        toggle={() => {
          toggleNotificationModal();
        }}
        centered
      >
        <ModalHeader className="bg-light p-3">
          {t("Notifications.Send")}
          <Button
            type="button"
            onClick={() => {
              setIsNotificationModalOpen(false);
            }}
            className="btn-close"
            aria-label={t("Common.Close")}
          ></Button>
        </ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            notificationForm.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalBody>
            {sendingNotificationError && sendingNotificationError ? (
              <Alert color="danger">
                {" "}
                {t(`Messages.Error.${sendingNotificationError}`)}{" "}
              </Alert>
            ) : null}
            <div className="mb-3">
              <Label htmlFor="title" className="form-label">
                {t("Fields.Title")}*
              </Label>
              <Input
                type="text"
                className="form-control"
                id="title"
                placeholder={t("Fields.Enter Title")}
                value={notificationForm.values.title || ""}
                onChange={notificationForm.handleChange}
                onBlur={notificationForm.handleBlur}
                invalid={
                  notificationForm.touched.title &&
                  notificationForm.errors.title
                    ? true
                    : false
                }
              />
              {notificationForm.touched.title &&
              notificationForm.errors.title ? (
                <FormFeedback type="invalid">
                  {notificationForm.errors.title}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="arabicTitle" className="form-label">
                {t("Fields.Arabic Title")}*
              </Label>
              <Input
                type="text"
                className="form-control"
                id="arabicTitle"
                placeholder={t("Fields.Enter Arabic Title")}
                value={notificationForm.values.arabicTitle || ""}
                onChange={notificationForm.handleChange}
                onBlur={notificationForm.handleBlur}
                invalid={
                  notificationForm.touched.arabicTitle &&
                  notificationForm.errors.arabicTitle
                    ? true
                    : false
                }
              />
              {notificationForm.touched.arabicTitle &&
              notificationForm.errors.arabicTitle ? (
                <FormFeedback type="invalid">
                  {notificationForm.errors.arabicTitle}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="body" className="form-label">
                {t("Fields.Body")}*
              </Label>
              <Input
                type="textarea"
                row="2"
                className="form-control"
                id="body"
                placeholder={t("Fields.Enter message body")}
                value={notificationForm.values.body || ""}
                onChange={notificationForm.handleChange}
                onBlur={notificationForm.handleBlur}
                invalid={
                  notificationForm.touched.body && notificationForm.errors.body
                    ? true
                    : false
                }
              />
              {notificationForm.touched.body && notificationForm.errors.body ? (
                <FormFeedback type="invalid">
                  {notificationForm.errors.body}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="arabicBody" className="form-label">
                {t("Fields.Arabic Body")}*
              </Label>
              <Input
                type="textarea"
                row="2"
                className="form-control"
                id="arabicBody"
                placeholder={t("Fields.Enter message body in arabic")}
                value={notificationForm.values.arabicBody || ""}
                onChange={notificationForm.handleChange}
                onBlur={notificationForm.handleBlur}
                invalid={
                  notificationForm.touched.arabicBody &&
                  notificationForm.errors.arabicBody
                    ? true
                    : false
                }
              />
              {notificationForm.touched.arabicBody &&
              notificationForm.errors.arabicBody ? (
                <FormFeedback type="invalid">
                  {notificationForm.errors.arabicBody}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="hstack gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setIsNotificationModalOpen(false)}
              >
                {t("Common.Close")}
              </button>
              <button
                type="submit"
                className="btn btn-success"
                id="add-btn"
                disabled={sendingNotification}
              >
                {sendingNotification ? (
                  <span className="d-flex align-items-center">
                    <span className="flex-grow-1 me-2">
                      {t("Common.Sending")}...
                    </span>
                    <Spinner size="sm" className="flex-shrink-0" role="status">
                      {" "}
                      {t("Common.Sending")}
                      ...{" "}
                    </Spinner>
                  </span>
                ) : (
                  <>{t("Common.Send")}</>
                )}
              </button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>
      {/* Block Unblock Modal */}
      <Modal
        isOpen={isBlockModalOpen}
        toggle={() => {
          toggleBlockUnblockModal();
        }}
        centered
      >
        <ModalHeader className="bg-light p-3">
          {t("Common.Block - Unblock")}
          <Button
            type="button"
            onClick={() => {
              setIsBlockModalOpen(false);
            }}
            className="btn-close"
            aria-label={t("Common.Close")}
          ></Button>
        </ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            blockUnblockForm.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalBody>
            {updateBlockStatusError ? (
              <Alert color="danger">
                {" "}
                {t(`Messages.Error.${updateBlockStatusError}`)}{" "}
              </Alert>
            ) : null}
            <div className="d-flex">
              <div className="form-check mb-4 mt-2">
                <Input
                  className="form-check-input"
                  type="checkbox"
                  id="isBlock"
                  checked={blockUnblockForm.values.isBlock}
                  value={blockUnblockForm.values.isBlock}
                  onChange={(e) => onChangeIsBlock(e)}
                  onBlur={blockUnblockForm.handleBlur}
                />
                <Label className="form-check-label" htmlFor="isBlock">
                  {t("User.Block")}{" "}
                </Label>
              </div>
            </div>
            <div className="mb-3">
              <Label htmlFor="blockReason" className="form-label">
                {t("Fields.Block Reason")}*
              </Label>
              <Input
                type="textarea"
                className="form-control"
                id="blockReason"
                placeholder={t("Fields.Enter Block Reason")}
                value={blockUnblockForm.values.blockReason || ""}
                onChange={blockUnblockForm.handleChange}
                onBlur={blockUnblockForm.handleBlur}
                invalid={
                  blockUnblockForm.touched.blockReason &&
                  blockUnblockForm.errors.blockReason
                    ? true
                    : false
                }
              />
              {blockUnblockForm.touched.blockReason &&
              blockUnblockForm.errors.blockReason ? (
                <FormFeedback type="invalid">
                  {blockUnblockForm.errors.blockReason}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Label htmlFor="blockReasonArabic" className="form-label">
                {t("Fields.Block Reason Arabic")}*
              </Label>
              <Input
                type="textarea"
                className="form-control"
                id="blockReasonArabic"
                placeholder={t("Fields.Enter Block Reason in Arabic")}
                value={blockUnblockForm.values.blockReasonArabic || ""}
                onChange={blockUnblockForm.handleChange}
                onBlur={blockUnblockForm.handleBlur}
                invalid={
                  blockUnblockForm.touched.blockReasonArabic &&
                  blockUnblockForm.errors.blockReasonArabic
                    ? true
                    : false
                }
              />
              {blockUnblockForm.touched.blockReasonArabic &&
              blockUnblockForm.errors.blockReasonArabic ? (
                <FormFeedback type="invalid">
                  {blockUnblockForm.errors.blockReasonArabic}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="hstack gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => toggleBlockUnblockModal()}
              >
                {t("Common.Close")}
              </button>
              <button
                type="submit"
                className="btn btn-success"
                id="add-btn"
                disabled={updatingBlockStatus}
              >
                {updatingBlockStatus ? (
                  <span className="d-flex align-items-center">
                    <span className="flex-grow-1 me-2">
                      {t("Common.Saving")}...
                    </span>
                    <Spinner size="sm" className="flex-shrink-0" role="status">
                      {" "}
                      {t("Common.Saving")}
                      ...{" "}
                    </Spinner>
                  </span>
                ) : (
                  <>{t("Common.Save")}</>
                )}
              </button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Purchases Modal */}
      <Modal
        isOpen={isPurchaseModalOpen}
        toggle={() => {
          togglePurchasesModal();
        }}
        centered
        size="xl"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            togglePurchasesModal();
          }}
        >
          {t("Categories.Categories")}
        </ModalHeader>
        <ModalBody>
          {technicianPurchasesLoading ? (
            <div className="d-flex justify-content-center">
              <Spinner />
            </div>
          ) : technicianPurchases && technicianPurchases.length ? (
            <>
              <Row>
                <h4>{t("Common.Purchased")}</h4>
                {technicianPurchases
                  .filter(
                    (tp) =>
                      tp.totalCalls != null &&
                      tp.totalCalls !== undefined &&
                      tp.totalCalls > 0
                  )
                  .map((item, index) => (
                    <Col lg={3} className="py-2" key={index + 1}>
                      <Row>
                        <Col className="d-flex justify-content-center">
                          <div className="team-profile-img">
                            <div className="avatar-lg  rounded-circle flex-shrink-0">
                              {item.icon != null ? (
                                <img
                                  src={item.icon}
                                  alt=""
                                  className="img-fluid d-block "
                                />
                              ) : (
                                <div className="avatar-title text-uppercase border rounded-circle bg-light text-primary">
                                  {item.title.charAt(0) +
                                    item.title
                                      .split(" ")
                                      .slice(-1)
                                      .toString()
                                      .charAt(0)}
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <div className="text-center">
                            <div className="team-content pt-2">
                              <h3 className={`fs-14 mb-1 `}>
                                {i18n.language === "ar"
                                  ? item.arabicTitle
                                  : item.title}
                              </h3>
                            </div>
                            <div className="team-content pt-1">
                              {item.isFreeOfCost && (
                                <h4 className="fs-14 mb-1">
                                  {t("Common.Free")}
                                </h4>
                              )}

                              {!item.isFreeOfCost && (
                                <h4 className={`fs-12 mb-1 mt-2`}>
                                  {t("Common.Total Purchased Calls")}{" "}
                                  {item.totalCalls !== null &&
                                  item.totalCalls !== undefined
                                    ? item.totalCalls
                                    : 0}
                                </h4>
                              )}
                              <React.Fragment>
                                <h4 className={`fs-12 mb-1 mt-1`}>
                                  {t("Common.Remaining Calls")}{" "}
                                </h4>
                                {changeTechnicianRemainingCallsLoading ? (
                                  <Spinner />
                                ) : (
                                  <>
                                    {!item.isFreeOfCost && (
                                      <div className="input-step step-primary">
                                        <button
                                          type="button"
                                          className="minus"
                                          style={{
                                            opacity:
                                              item.remainingCalls == 0
                                                ? 0.5
                                                : 1,
                                          }}
                                          disabled={item.remainingCalls === 0}
                                          onClick={() => {
                                            countDown(item.id);
                                          }}
                                        >
                                          –
                                        </button>
                                        <Input
                                          type="number"
                                          className="product-quantity"
                                          value={
                                            item.remainingCalls != null &&
                                            item.remainingCalls !== undefined
                                              ? item.remainingCalls
                                              : 0
                                          }
                                          min={0}
                                          max={
                                            item.totalCalls != null &&
                                            item.totalCalls !== undefined
                                              ? item.totalCalls
                                              : 0
                                          }
                                          readOnly
                                        />
                                        <button
                                          type="button"
                                          className="plus"
                                          style={{
                                            opacity:
                                              item.totalCalls ==
                                              item.remainingCalls
                                                ? 0.5
                                                : 1,
                                          }}
                                          disabled={
                                            item.totalCalls ==
                                            item.remainingCalls
                                          }
                                          onClick={() => {
                                            countUP(item.id);
                                          }}
                                        >
                                          +
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )}
                              </React.Fragment>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  ))}
              </Row>
              <Row>
                <h4 className="my-3">{t("Common.Not Purchased")}</h4>
                {technicianPurchases
                  .filter(
                    (tp) =>
                      tp.totalCalls === null ||
                      tp.totalCalls === undefined ||
                      tp.totalCalls <= 0
                  )
                  .map((item, index) => (
                    <Col lg={3} className="py-2" key={index + 1}>
                      <Row>
                        <Col className="d-flex justify-content-center">
                          <div className="team-profile-img">
                            <div className="avatar-lg  rounded-circle flex-shrink-0">
                              {item.icon != null ? (
                                <img
                                  src={item.icon}
                                  alt=""
                                  className="img-fluid d-block "
                                />
                              ) : (
                                <div className="avatar-title text-uppercase border rounded-circle bg-light text-primary">
                                  {item.title.charAt(0) +
                                    item.title
                                      .split(" ")
                                      .slice(-1)
                                      .toString()
                                      .charAt(0)}
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <div className="text-center">
                            <div className="team-content pt-2">
                              <h3 className={`fs-14 mb-1`}>
                                {i18n.language === "ar"
                                  ? item.arabicTitle
                                  : item.title}
                              </h3>
                            </div>
                            <div className="team-content pt-1">
                              {item.isFreeOfCost && (
                                <h4 className="fs-14 mb-1">
                                  {t("Common.Free")}
                                </h4>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  ))}
              </Row>
            </>
          ) : (
            <div className="d-flex justify-content-center">
              {t("Pagination.There are no records to display")}
            </div>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default TechniciansPage;
